import { IActionResponse } from "../../dal/BaseDAL";
import { ICollectionResponse } from "../../models/ICollectionResponse";
import api, {LIST, WORKSPACE_PRODUCTS_TYPE, WORKSPACES_TYPE, PRODUCT_BUNDLE_REFS_TYPE} from "../Api";
import {
    IAddProductToBundleResponse,
    IProductBundle,
    IProductBundleItem,
    IProductBundleRef
} from "../../models/ProductBundles";
import {IBundleItemPrice, IStorefrontBundleEstimate} from "../../models/Storefront";
import {IWorkspaceItem, IWorkspaceItemBundleSelection} from "../../models/Workspace";
import {cloneDeep} from "lodash";

export const productBundleApi = api.injectEndpoints({
    endpoints: (build) => ({
        getProductBundleRefs: build.query<ICollectionResponse<IProductBundleRef>, { listedInStorefront?: boolean, storefronts?: string[], keywords?: string}>({
            query: (arg) => {
                const storefronts = arg.storefronts && arg.storefronts.length > 0 ? `${arg.storefronts.join(',')}` : undefined;
                return  `/productBundles/refs?listedInStorefront=${arg.listedInStorefront ?? false}`.appendIfDefined(storefronts, `&storefronts=(${storefronts})`).appendIfDefined(arg.keywords, `&keywordSearch=${encodeURIComponent(arg.keywords ?? '')}`);
            },
            // Default per-arg cache keys: each filter combination is its own entry, so
            // concurrent queries with different args (e.g. the filtered list and the
            // unfiltered count) don't overwrite each other's results.
            providesTags: [{ type: PRODUCT_BUNDLE_REFS_TYPE, id: LIST }],
        }),
        getTopProductBundleRefs: build.query<ICollectionResponse<IProductBundleRef>, { listedInStorefront?: boolean, storefronts?: string[], keywords?: string}>({
            query: (arg) => {
                const storefronts = arg.storefronts && arg.storefronts.length > 0 ? `${arg.storefronts.join(',')}` : undefined;
                return  `/productBundles/top?listedInStorefront=${arg.listedInStorefront ?? false}`.appendIfDefined(storefronts, `&storefronts=(${storefronts})`).appendIfDefined(arg.keywords, `&keywordSearch=${encodeURIComponent(arg.keywords ?? '')}`);
            },
            providesTags: [{ type: PRODUCT_BUNDLE_REFS_TYPE, id: 'TOP' }],
        }),
        getProductBundle: build.query<IProductBundle, string>({
            query: (arg) => `/productBundles/${arg}`,
            providesTags: (result, error, id) => [{ type: 'ProductBundles', id }]
        }),
        checkProductBundleId: build.query<boolean, string>({
            query: (arg) => `/productBundles/${arg}/validate`
        }),
        getProductBundleItems: build.query<ICollectionResponse<IProductBundleItem>, string>({
            query: (arg) => `/productBundles/${arg}/items`,
            providesTags: (result, error, id) => [{ type: 'ProductBundleItems', id }]
        }),
        estimateProductBundlePriceCost: build.mutation<IStorefrontBundleEstimate, {bundleId: string, request: {markupType?: string, markup?: number, minimumPrice?: number, selection?: IWorkspaceItemBundleSelection[], quantity?: number, /** Price as an always-included line (unsourceable items at lowest known cost). */ alwaysAvailable?: boolean, /** Resolves the storefront's markup and this bundle's markup for it. */ storefrontId?: string}}>({
            query: (arg) => ({
                url: `/productBundles/${arg.bundleId}/estimate`,
                body: arg.request,
                method: 'POST',
            }),
        }),
        getProductBundleItemPrices: build.query<ICollectionResponse<IBundleItemPrice>, {bundleId: string, markup?: number, markupType?: string, storefrontId?: string}>({
            query: (arg) => {
                const params = new URLSearchParams();
                if (arg.markup != undefined) params.append('markup', arg.markup.toString());
                if (arg.markupType) params.append('markupType', arg.markupType);
                if (arg.storefrontId) params.append('storefrontId', arg.storefrontId);
                const qs = params.toString();
                return `/productBundles/${arg.bundleId}/itemPrices${qs ? `?${qs}` : ''}`;
            },
            providesTags: (result, error, arg) => [{ type: 'ProductBundleItems', id: arg.bundleId }],
        }),
        clearBundleCache: build.mutation<null, void>({
            queryFn: () => ({data: null}),
            invalidatesTags: [
                {type: WORKSPACES_TYPE, id: LIST},
                {type: WORKSPACE_PRODUCTS_TYPE, id: LIST},
            ]
        }),
        clearBundleRefsCache: build.mutation<null, void>({
            queryFn: () => ({data: null}),
            invalidatesTags: [
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: LIST },
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: 'TOP' },
            ],
        }),
        patchProductBundle: build.mutation<IActionResponse, {id: string, request: Partial<IProductBundle>}>({
            query: (arg) => ({
                url: `/productBundles/${arg.id}/`,
                body: arg.request,
                method: 'PATCH',
            }),
            invalidatesTags: [
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: LIST },
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: 'TOP' },
            ],
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {

                const result = dispatch(
                    productBundleApi.util.updateQueryData('getProductBundle', arg.id, draft => {
                        const copy = {...draft, ...arg.request};
                        Object.assign(draft, copy);
                    })
                );
                
                const refsUpdate = dispatch(
                    productBundleApi.util.updateQueryData('getProductBundleRefs', {}, draft => {
                        const itemIndex = draft.items.findIndex(i => i.id === arg.id);
                        if (itemIndex !== -1) {
                            const item = {...draft.items[itemIndex]};
                            draft.items.splice(itemIndex, 1);
                            
                            if (arg.request.name !== undefined) {
                                item.name = arg.request.name;
                            }
                            if (arg.request.imageUrl !== undefined) {
                                item.imageUrl = arg.request.imageUrl;
                            }
                            draft.items.unshift(item);
                        }
                    })
                );
                try{
                    await queryFulfilled;
                }
                catch {
                    result.undo()
                    refsUpdate.undo();
                }
            }
        }),
        deleteProductBundleItem: build.mutation<IActionResponse, {bundleId: string, productBundleItem: string}>({
            query: (arg) => ({
                url: `/productBundles/${arg.bundleId}/items/${arg.productBundleItem}`,
                method: 'DELETE',
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const refsUpdate = dispatch(
                    productBundleApi.util.updateQueryData('getProductBundleItems', arg.bundleId, draft => {
                        const itemIndex = draft.items.findIndex(i => i.id === arg.productBundleItem);
                        if (itemIndex !== -1) {
                            draft.items.splice(itemIndex, 1);
                        }
                    })
                );
                try{
                    await queryFulfilled;
                }
                catch {
                    refsUpdate.undo();
                }
            }
        }),
        deleteProductBundle: build.mutation<IActionResponse, {id: string}>({
            query: (arg) => ({
                url: `/productBundles/${arg.id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'ProductBundles', id: arg.id },
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: LIST },
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: 'TOP' },
            ],
            onQueryStarted: async (arg, {dispatch, getState, queryFulfilled}) => {
                // Every filter combination is its own cache entry, so optimistically
                // remove the bundle from all cached refs lists, not just the `{}` one.
                const undos = productBundleApi.util
                    .selectInvalidatedBy(getState(), [
                        { type: PRODUCT_BUNDLE_REFS_TYPE, id: LIST },
                        { type: PRODUCT_BUNDLE_REFS_TYPE, id: 'TOP' },
                    ])
                    .filter(({endpointName}) => endpointName === 'getProductBundleRefs' || endpointName === 'getTopProductBundleRefs')
                    .map(({endpointName, originalArgs}) => dispatch(
                        productBundleApi.util.updateQueryData(endpointName as 'getProductBundleRefs', originalArgs, draft => {
                            const itemIndex = draft.items.findIndex(i => i.id === arg.id);
                            if (itemIndex !== -1) {
                                draft.items.splice(itemIndex, 1);
                            }
                        })
                    ));
                try{
                    await queryFulfilled;
                }
                catch {
                    undos.forEach(u => u.undo());
                }
            }
        }),
        postProductBundle: build.mutation<IActionResponse, {request: Partial<IProductBundle>}>({
            query: (arg) => ({
                url: `/productBundles`,
                body: arg.request,
                method: 'POST',
            }),
            // Refetch every cached refs list (each filter combination is its own
            // cache entry) rather than hand-patching a single entry.
            invalidatesTags: [
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: LIST },
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: 'TOP' },
            ],
        }),

        addItemToProductBundle: build.mutation<IAddProductToBundleResponse, {bundleId: string, catalog: string, productId: string}>({
            query: (arg) => ({
                url: `/catalog/${arg.catalog}/products/${arg.productId}/addToBundle/${arg.bundleId}`,
                method: 'POST',
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const result = await queryFulfilled;
                    if (!result.data?.item) {
                        return;
                    }
                    // Invalidate the cache for the specific bundle
                    dispatch(
                        productBundleApi.util.updateQueryData('getProductBundleItems', arg.bundleId, (draft) => {
                            const copy = cloneDeep(draft)
                            if (!result.data?.item || !result.data.success) {
                                return;
                            }
                            const item = result.data.item;
                            if (!item.id) return;
                            const existingCheck = copy.items.find(i => i.id === item.id);
                            if(existingCheck?.details?.quantity) {
                                existingCheck.details.quantity += item.details?.quantity ?? 1;
                            }
                            else {
                                copy.items.push(result.data.item);
                            }
                            Object.assign(draft, copy)
                        }
                    ));
                } catch {
                    // Handle error if needed
                }
            }
        }),
        addSavedSearchToProductBundle: build.mutation<IActionResponse, {bundleId: string, savedSearchId: string}>({
            query: (arg) => ({
                url: `/productBundles/${arg.bundleId}/savedSearch/${arg.savedSearchId}`,
                method: 'POST',
            }),
            // The added saved-search item is resolved server-side (its representative product depends on
            // the storefront), so refetch the bundle's items rather than patching the cache in place.
            invalidatesTags: (result, error, arg) => [{ type: 'ProductBundleItems', id: arg.bundleId }],
        }),
        patchProductBundleItem: build.mutation<IActionResponse, {bundleId: string, productId: string, request: Partial<IProductBundleItem>}>({
            query: (arg) => ({
                url: `/productBundles/${arg.bundleId}/items/${arg.productId}`,
                body: arg.request,
                method: 'PATCH',
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {

                    // Invalidate the cache for the specific bundle
                    const result = dispatch(
                        productBundleApi.util.updateQueryData('getProductBundleItems', arg.bundleId, (draft) => {
                            const itemIndex = draft.items.findIndex(i => i.id === arg.productId);
                            if (itemIndex !== -1) {
                                const copy = cloneDeep(draft)
                                const item = {...copy.items[itemIndex], details:{...copy.items[itemIndex].details, ...arg.request.details}, ...arg.request};
                                copy.items.splice(itemIndex, 1, item);
                                Object.assign(draft, copy);
                            }
                        })
                    );
                try {
                    await queryFulfilled;
                } catch {
                    result.undo()
                }
            }
        }),

        addProductBundleToWorkspace: build.mutation<IWorkspaceItem, {bundleId: string, workspaceId: string}>({
            query: (arg) => ({
                url: `/productBundles/${arg.bundleId}/addToWorkspace/${arg.workspaceId}`,
                method: 'POST',
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const result = await queryFulfilled;
                if (!result.data) {
                    return;
                }
                dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspaceId, draft => {
                        // Add a new facade for the bundle
                        const initial = [...draft]
                        initial.push(result.data)
                        Object.assign(draft, initial);
                    })
                );
            }
        }),
        putProductBundle: build.mutation<IActionResponse, {id: string, request: IProductBundle}>({
            query: (arg) => ({
                url: `/productBundles/${arg.id}/`,
                body: arg.request,
                method: 'PUT',
            }),
            invalidatesTags: [
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: LIST },
                { type: PRODUCT_BUNDLE_REFS_TYPE, id: 'TOP' },
            ],
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const result = dispatch(
                    productBundleApi.util.updateQueryData('getProductBundle', arg.id, draft => {
                        const copy = {...draft, ...arg.request};
                        Object.assign(draft, copy);
                    })
                );
                const refsUpdate = dispatch(
                    productBundleApi.util.updateQueryData('getProductBundleRefs', {}, draft => {
                        const item = draft.items.find(i => i.id === arg.id);
                        if (item) {
                            const newItem = {...arg.request}
                            Object.assign(item, newItem);
                        }
                    })
                );
                try {
                    await queryFulfilled
                }
                catch {
                    result.undo()
                    refsUpdate.undo();
                }
            }
        }),
    })
})

export const {
    useGetProductBundleRefsQuery,
    useGetProductBundleQuery,
    // The bundle PATCH replaces details wholesale, so anything adding a storefront to a bundle has to read the
    // current details first. Callers doing that for a handful of bundles in a row need the lazy trigger.
    useLazyGetProductBundleQuery,
    usePatchProductBundleMutation,
    usePutProductBundleMutation,
    useGetProductBundleItemsQuery,
    useGetProductBundleItemPricesQuery,
    useGetTopProductBundleRefsQuery,
    usePostProductBundleMutation,
    useDeleteProductBundleMutation,
    useDeleteProductBundleItemMutation,
    useAddItemToProductBundleMutation,
    useAddSavedSearchToProductBundleMutation,
    useAddProductBundleToWorkspaceMutation,
    useEstimateProductBundlePriceCostMutation,
    usePatchProductBundleItemMutation,
    useClearBundleCacheMutation,
    useClearBundleRefsCacheMutation,
    useCheckProductBundleIdQuery
} = productBundleApi;